<template>
  <div class="panel-card home-panel rank-panel">
    <div class="panel-head">
      <h3 class="panel-title">{{ title }}</h3>

      <div v-if="hasFilters" class="seg">
        <button
          v-for="item in filters"
          :key="item.value"
          :class="{ on: item.value === activeFilter }"
          @click="$emit('filter-change', item.value)"
        >
          {{ item.label }}
        </button>
      </div>

      <div v-else-if="showHeaderPlaceholder" class="seg seg--ghost" aria-hidden="true">
        <button type="button" :style="placeholderStyle">{{ placeholderText }}</button>
      </div>
    </div>

    <ul class="rank-list">
      <li class="rank-item" :class="itemRowClass" v-for="(item, idx) in items" :key="rowKey(item, idx)">
        <span class="rank-num">{{ idx + 1 }}</span>
        <span class="rank-name">{{ resolveName(item) }}</span>
        <span class="rank-metric">{{ resolveMetric(item) }}</span>

        <button v-if="showAction(item)" class="btn s xs" @click="$emit('action', item)">
          {{ actionText }}
        </button>
        <span v-else-if="showActionColumn" class="action-placeholder" aria-hidden="true"></span>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: "RankingPanel",
  props: {
    title: {
      type: String,
      required: true
    },
    items: {
      type: Array,
      default: () => []
    },
    config: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    filters() {
      return Array.isArray(this.config.filters) ? this.config.filters : [];
    },
    hasFilters() {
      return this.filters.length > 0;
    },
    activeFilter() {
      return this.config.activeFilter;
    },
    showHeaderPlaceholder() {
      return this.config.showHeaderPlaceholder !== false;
    },
    placeholderText() {
      return this.config.placeholderText || "placeholder";
    },
    placeholderStyle() {
      const width = Number(this.config.headerRightWidth) || 186;
      return { width: `${width}px` };
    },
    actionText() {
      return this.config.actionText || "详情";
    },
    hasAction() {
      return Boolean(this.config.actionKey);
    },
    showActionColumn() {
      return this.hasAction || Boolean(this.config.reserveActionSpace);
    },
    itemRowClass() {
      return this.showActionColumn ? "rank-item--action" : "rank-item--plain";
    },
    nameKey() {
      return this.config.nameKey || "name";
    },
    metricKey() {
      return this.config.metricKey || "count";
    },
    metricSuffix() {
      return this.config.metricSuffix || "";
    }
  },
  methods: {
    rowKey(item, idx) {
      const key = this.config.rowKey || this.nameKey;
      return `${item?.[key] ?? "row"}-${idx}`;
    },
    resolveName(item) {
      return item?.[this.nameKey] ?? "-";
    },
    resolveMetric(item) {
      const value = item?.[this.metricKey] ?? 0;
      if (typeof this.config.metricFormatter === "function") {
        return this.config.metricFormatter(item, value);
      }
      return `${value} ${this.metricSuffix}`.trim();
    },
    showAction(item) {
      return this.hasAction && Boolean(item?.[this.config.actionKey]);
    }
  }
};
</script>

<style scoped>
.rank-panel {
  display: flex;
  flex-direction: column;
  min-height: 320px;
  height: 100%;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: nowrap;
  margin-bottom: 8px;
  min-height: 38px;
}

.seg {
  display: flex;
  gap: 6px;
  flex-wrap: nowrap;
  flex-shrink: 0;
}

.seg button {
  border: 1px solid #d6e6df;
  background: #fff;
  color: #475569;
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 12px;
  cursor: pointer;
}

.seg button.on {
  background: linear-gradient(130deg, #0f766e, #14b8a6);
  border-color: transparent;
  color: #fff;
}

.seg--ghost {
  visibility: hidden;
}

.rank-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 8px;
  align-content: start;
  flex: 1;
  min-height: 0;
  max-height: 300px;
  overflow-y: auto;
  padding-right: 4px;
}

.rank-list::-webkit-scrollbar {
  width: 6px;
}

.rank-list::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 3px;
}

.rank-list::-webkit-scrollbar-thumb {
  background: rgba(15, 118, 110, 0.3);
  border-radius: 3px;
}

.rank-list::-webkit-scrollbar-thumb:hover {
  background: rgba(15, 118, 110, 0.5);
}

.rank-item {
  display: grid;
  gap: 8px;
  align-items: center;
  background: #fcfffd;
  border: 1px solid #eaf2ef;
  border-radius: 11px;
  padding: 8px;
  min-height: 50px;
  transition: transform 0.15s ease, box-shadow 0.2s ease;
}

.rank-item--action {
  grid-template-columns: 28px minmax(0, 1fr) minmax(84px, auto) 56px;
}

.rank-item--plain {
  grid-template-columns: 28px minmax(0, 1fr) minmax(84px, auto);
}

.rank-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 14px rgba(15, 23, 42, 0.08);
}

.rank-num {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  color: #fff;
  background: linear-gradient(130deg, #0f766e, #0ea5e9);
  font-size: 12px;
}

.rank-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rank-metric {
  color: #0f766e;
  font-weight: 700;
  font-size: 12px;
  justify-self: end;
  text-align: right;
}

.action-placeholder {
  display: inline-block;
  width: 56px;
  height: 30px;
  visibility: hidden;
}

@media (max-width: 1100px) {
  .panel-head {
    flex-wrap: wrap;
  }
}
</style>
